import type { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  EventCardActions,
  EventCardBadge,
  EventCardDate,
  EventCardDetails,
  EventCardDetailsSpacer,
  EventCardImage,
  EventCardLink,
  EventCardLocation,
  EventCardPrice,
  EventCardPromoted,
  EventCardSaveAction,
  EventCardShareAction,
  EventCardTitle,
  EventCardTracking,
  EventCardUrgencySignals,
  getEventCardLinkProps,
  getFormattedSalesStatus,
  getShareActionProps,
  HORIZONTAL_CARD_BADGE_STYLE,
  HorizontalEventCard,
  HorizontalEventCardColumn,
  HORIZONTAL_LEFT_CARD_BADGE_STYLE,
  useFormattedLocationString,
  useGetFormattedEventDateTimeString,
  useMinPriceString,
} from '@eventbrite/consumer-search.event-renderer';
import { HORIZONTAL_EVENT_CARD_IMAGE_CONFIG } from '@eventbrite/consumer-search.event-renderer/constants';
import type { FormattedEvent } from '@eventbrite/consumer-search.event-renderer';

import { getEventCardSignalsVisibility } from '../getEventCardSignalsVisibility';
import type { AuthState } from '../EventsBucket/EventsBucket';
import styles from './OrganizerHorizontalEventCard.module.scss';

const messages = defineMessages({
  viewEvent: {
    id: 'View {eventName}',
    defaultMessage: 'View {eventName}',
  },
});

interface Props {
  event: FormattedEvent;
  locale: string;
  statsigLocationString: string;
  bucketName: string;
  authState?: AuthState;
  isLazyImage?: boolean;
  affCode?: string;
  showSaveAction?: boolean;
  showShareAction?: boolean;
  moreActions?: ReactNode;
  leadingContent?: ReactNode;
  variant?: 'default' | 'highlighted';
  shareOptions?: {
    utmOptions?: Record<string, string>;
  };
}

export function OrganizerHorizontalEventCard({
  event,
  locale,
  statsigLocationString,
  bucketName,
  authState = 'unknown',
  isLazyImage = false,
  affCode = 'ebdsoporgprofile',
  showSaveAction = false,
  showShareAction = false,
  moreActions,
  leadingContent,
  variant = 'default',
  shareOptions = {},
}: Props) {
  const intl = useIntl();
  const isHighlighted = variant === 'highlighted';
  const canRenderActions = authState !== 'unknown';
  const isAuthenticated = authState === 'authenticated';
  const { utmOptions } = shareOptions;
  const { showUrgencySignals } = getEventCardSignalsVisibility({
    event,
    withLimitedVisibility: false,
  });
  const linkProps = getEventCardLinkProps({
    affCode,
    event,
    label: intl.formatMessage(messages.viewEvent, { eventName: event.name }),
  });
  const formattedDateString = useGetFormattedEventDateTimeString({
    startDate: event?.startDate,
    startTime: event?.startTime,
    timezone: event?.timezone,
    additionalSessionsCount: event?.additionalSessionsCount,
    locale,
    useUserTimezone: event.isOnlineEvent,
    dateTimeDelimeter: '\u2022',
  });
  const formattedPriceRangeString = useMinPriceString({
    price: event?.minPrice,
    locale,
  });
  const formattedLocationString = useFormattedLocationString({
    venue: event.venue,
    style: 'venue',
  });
  const formattedSalesStatus = getFormattedSalesStatus(event);
  const { urgencySignals } = event;
  const shouldShowUrgencySignals = !!urgencySignals && !formattedSalesStatus && showUrgencySignals;
  const eventCardTrackingProps = {
    statsigLocationString,
    event,
    eventBucketLabel: bucketName,
  };

  return (
    <section
      className={`organizer-horizontal-event-card ${styles.cardRoot} ${isHighlighted ? styles.highlightedCard : ''}`}
      data-variant={variant}
    >
      <HorizontalEventCard actionVisibility="hidden">
        <EventCardTracking {...eventCardTrackingProps}>
          <HorizontalEventCardColumn>
            <div className={styles.imageColumn}>
              <EventCardBadge
                style={moreActions ? HORIZONTAL_CARD_BADGE_STYLE : HORIZONTAL_LEFT_CARD_BADGE_STYLE}
              >
                {formattedSalesStatus}
              </EventCardBadge>
              <EventCardLink {...linkProps}>
                <EventCardImage
                  name={event.name}
                  loading={isLazyImage ? 'lazy' : 'eager'}
                  src={event.imageUrl}
                  imageSizes={event.imageSizes}
                  tokens={{
                    ...HORIZONTAL_EVENT_CARD_IMAGE_CONFIG,
                    '--image-background-color': event.edgeColor,
                    '--image-object-position': event.focalPoint
                      ? `${event.focalPoint.x * 100}% ${event.focalPoint.y * 100}%`
                      : undefined,
                  }}
                />
              </EventCardLink>
              {moreActions ? <div className={styles.pinAction}>{moreActions}</div> : null}
            </div>
          </HorizontalEventCardColumn>
          <HorizontalEventCardColumn
            tokens={{
              '--HorizontalCardColumnFlexShrink': '1',
            }}
          >
            <div className={styles.detailsColumn}>
              <EventCardDetails
                space="xxs-spacing"
                tokens={{
                  '--EventCardDetailsPadding': isHighlighted
                    ? '24px 24px 24px 12px'
                    : '4px 16px 16px 16px',
                  '--EventCardDetailsFlexGrow': '1',
                  '--EventCardDetailsPosition': 'relative',
                }}
              >
                {leadingContent}
                {shouldShowUrgencySignals ? (
                  <EventCardUrgencySignals
                    className={styles.urgencySignals}
                    urgencySignals={urgencySignals}
                  />
                ) : null}
                <EventCardLink {...linkProps}>
                  <EventCardTitle
                    color="neutral-800"
                    className="event-card__clamp-line--two"
                    title={event.name}
                    variant={isHighlighted ? 'heading-sm' : 'body-lg'}
                  />
                </EventCardLink>
                <EventCardDate
                  color="neutral-800"
                  formattedDateString={formattedDateString}
                  variant={isHighlighted ? 'body-lg-bold' : 'body-md-bold'}
                />
                <EventCardLocation
                  className="event-card__clamp-line--one"
                  formattedLocationString={formattedLocationString}
                  variant="body-md"
                />
                <EventCardDetailsSpacer />
                <div className={styles.priceWrapper}>
                  <EventCardPrice
                    color="neutral-800"
                    isLoading={false}
                    isFree={event.isFree}
                    formattedPriceString={formattedPriceRangeString}
                    variant={isHighlighted ? 'body-lg-bold' : 'body-md-bold'}
                  />
                </div>
                <EventCardPromoted
                  className={styles.promotedLabel}
                  isPromoted={event.isPromoted}
                  withUpsellModal={false}
                />

                {canRenderActions && (showSaveAction || showShareAction) && (
                  <EventCardActions
                    tokens={{
                      '--EventCardActionsPosition': 'absolute',
                      '--EventCardActionsBottom': isHighlighted ? '12px' : '8px',
                      '--EventCardActionsRight': isHighlighted ? '12px' : '8px',
                      '--EventCardActionsGap': isHighlighted ? '12px' : '8px',
                    }}
                    actionStyleOverride="outline"
                  >
                    {showSaveAction && (
                      <EventCardSaveAction
                        isAuthenticated={isAuthenticated}
                        savedByYou={event.savedByYou}
                        id={event.id}
                        eventName={event.name}
                        imageUrl={event.imageUrl}
                      />
                    )}
                    {showShareAction && (
                      <EventCardShareAction
                        {...getShareActionProps({
                          name: event.name,
                          id: event.id,
                          url: event.url,
                          affCode,
                          utmOptions,
                        })}
                      />
                    )}
                  </EventCardActions>
                )}
              </EventCardDetails>
            </div>
          </HorizontalEventCardColumn>
        </EventCardTracking>
      </HorizontalEventCard>
    </section>
  );
}
